import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Card,
  CircularProgress,
  Modal,
  Button
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apidelete, apiget, apigetBlob } from '../../service/api';
import Header from '../../components/Header';
import DeleteModel from '../../components/Deletemodle';

const StationLoanView = () => {
  const [loanData, setLoanData] = useState({});
  const [loading, setLoading] = useState(false);
  const [opendelete, setOpendelete] = useState(false);
  const [containerInfo, setContainerInfo] = useState(null);
  const [containerLoading, setContainerLoading] = useState(false);

  // Preview modal state
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState(null); // API route to fetch
  const [selectedTitle, setSelectedTitle] = useState('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null); // blob: URL for iframe
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const params = useParams();
  const navigate = useNavigate();

  const handleOpenDelete = () => setOpendelete(true);
  const handleCloseDelete = () => setOpendelete(false);

  const fetchLoan = async () => {
    try {
      setLoading(true);
      const res = await apiget(`cherrycreek/stationsloan/${params.id}`);
      if (res && res.status === 200) {
        setLoanData(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch loan:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContainerInfo = async (loanNumber) => {
    try {
      setContainerLoading(true);
      const res = await apiget(`cherrycreek/stationsloan/${loanNumber}/container`);
      if (res && res.status === 200) {
        setContainerInfo(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch container info:', err);
    } finally {
      setContainerLoading(false);
    }
  };

  const deleteLoan = async () => {
    await apidelete(`cherrycreek/stationsloan/${params.id}`);
    navigate('/dashboard/cherrycreek');
  };

  useEffect(() => {
    fetchLoan();
  }, []);

  useEffect(() => {
    if (loanData?.LenderLoanNumber) {
      fetchContainerInfo(loanData.LenderLoanNumber);
    }
  }, [loanData.LenderLoanNumber]);

  // Build preview URL for a given docId using the SAME relative base you use with apiget
  const makePreviewUrl = (docId) =>
    `cherrycreek/stationsloan/${loanData?.LenderLoanNumber}/doc/${docId}/preview`;

  // If backend returns `documents`, use that; otherwise derive simple groups from pages (fallback)
  const documents = useMemo(() => {
    if (containerInfo?.documents) return containerInfo.documents;

    const pages = containerInfo?.pages ?? [];
    if (!pages.length || !loanData?.LenderLoanNumber) return [];

    const grouped = pages.reduce((acc, p) => {
      (acc[p.xDocId] ||= []).push(p);
      return acc;
    }, /** @type {Record<string, any[]>} */ ({}));

    return Object.entries(grouped).map(([docId, docPages]) => ({
      docId,
      pageCount: docPages.length,
      previewUrl: makePreviewUrl(docId), // server merge route
      pages: docPages
    }));
  }, [containerInfo, loanData?.LenderLoanNumber]);

  // When a preview URL is selected, fetch it as a Blob and create an object URL for the iframe
  useEffect(() => {
    let revokeUrl;
    const load = async () => {
      if (!selectedPreviewUrl) return;
      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewBlobUrl(null);

      try {
        // Prefer using your configured apiget instance (axios) so baseURL/credentials apply
        
          const resp = await apigetBlob(selectedPreviewUrl, { responseType: 'blob' });
          const blobUrl = URL.createObjectURL(resp.data);
          setPreviewBlobUrl(blobUrl);
          revokeUrl = blobUrl;
         
      } catch (err) {
        console.error('Preview load failed:', err);
        setPreviewError('Unable to load preview.');
      } finally {
        setPreviewLoading(false);
      }
    };

    load();

    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
      setPreviewBlobUrl(null);
      setPreviewError(null);
      setPreviewLoading(false);
    };
  }, [selectedPreviewUrl]);

  return (
    <Container>
      <Grid container display="flex" alignItems="center">
        <Stack direction="row" alignItems="center" mb={3} justifyContent="space-between" width="100%">
          <Header title="Station Loan View" subtitle={`Loan ID: ${params.id}`} />
        </Stack>
      </Grid>

      {loading ? (
        <Modal open>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', p: 4, bgcolor: 'background.paper' }}>
            <CircularProgress />
          </Box>
        </Modal>
      ) : (
        <Card sx={{ p: 3 }}>
          <Typography variant="h6">Borrower Name: {loanData.Borrower1FullName}</Typography>
          <Typography variant="body1">Loan Number: {loanData.LenderLoanNumber}</Typography>
          <Typography variant="body1">Transaction ID: {loanData.TransactionID}</Typography>
          <Typography variant="body1">Investor Loan Number: {loanData.InvestorLoanNumber}</Typography>
          <Typography variant="body1">Loan Officer: {loanData.LoanOfficerName}</Typography>
          <Typography variant="body1">Current Station: {loanData.CurrentStationName}</Typography>
          <Typography variant="body1">Status: {loanData.TransactionStatusText}</Typography>
          <Typography variant="body1">MERS Number: {loanData.MERSNumber}</Typography>

          {containerLoading ? (
            <Box mt={2}>
              <CircularProgress size={20} />{' '}
              <Typography variant="body2" display="inline">
                Loading container info...
              </Typography>
            </Box>
          ) : containerInfo ? (
            <>
              <Typography variant="h6" mt={3}>Container Info</Typography>
              <Typography variant="body1">xContainerRef: {containerInfo.xContainerRef}</Typography>
              <Typography variant="body1">xContainerId: {containerInfo.xContainerId}</Typography>

              {/* Documents (doc-level merged PDF) */}
              {documents.length > 0 && (
                <>
                  <Typography variant="h6" mt={3}>Documents</Typography>
                  <Stack spacing={1} mt={1}>
                    {documents.map((doc) => (
                      <Stack
                        key={doc.docId}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Button
                          size="small"
                          variant="text"
                          sx={{ textTransform: 'none' }}
                          onClick={() => {
                            setSelectedTitle(`Document: ${doc.docId} (${doc.pageCount} page${doc.pageCount > 1 ? 's' : ''})`);
                            setSelectedPreviewUrl(doc.previewUrl); // set API route; Blob created by effect
                          }}
                        >
                          {doc.docId} — {doc.pageCount} page{doc.pageCount > 1 ? 's' : ''}
                        </Button>

                        {/* Optional: open in new tab (keep if you want raw navigation for debugging) */}
                        <Button
                          size="small"
                          onClick={() => window.open(doc.previewUrl, '_blank', 'noopener,noreferrer')}
                        >
                          Open
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </>
              )}

              {/* Fallback: show raw pages (if you still want per-page access). Note: TIFF likely downloads. */}
              {containerInfo.pages && containerInfo.pages.length > 0 && (
                <>
                  <Typography variant="subtitle1" mt={3}>Pages (raw TIFF links)</Typography>
                  <Stack spacing={1} mt={1}>
                    {containerInfo.pages.map((page, index) => (
                      <Box
                        key={index}
                        sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                        onClick={() => {
                          setSelectedTitle(`Page ${page.xPageIndex + 1}: ${page.xDocId}`);
                          setSelectedPreviewUrl(page.blobUrl); // will fetch blob; TIFF may not render inline
                        }}
                      >
                        Page {page.xPageIndex + 1}: {page.xDocId}
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" mt={3} color="text.secondary">
              No container information found.
            </Typography>
          )}
        </Card>
      )}

      {/* Preview Modal (merged PDF or raw TIFF fetched as Blob) */}
      <Modal open={!!selectedPreviewUrl} onClose={() => setSelectedPreviewUrl(null)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 2,
            maxWidth: '90%',
            maxHeight: '85%',
            overflow: 'auto',
            width: '100%'
          }}
        >
          <Typography variant="h6" mb={1}>{selectedTitle || 'Preview'}</Typography>

          {previewLoading && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading preview…</Typography>
            </Box>
          )}

          {previewError && (
            <Typography color="error" variant="body2" mb={2}>{previewError}</Typography>
          )}

          {previewBlobUrl && (
            <Box component="iframe" src={previewBlobUrl} width="100%" height="700px" />
          )}
        </Box>
      </Modal>
    </Container>
  );
};

export default StationLoanView;
